import type { GameWebSocket } from '../network/websocket';
import type { GameEngine } from '../game/engine';
import type {
  WorldEventStartPayload,
  WorldEventLinePayload,
  WorldEventRewardPayload,
  WorldEventEndPayload,
  HeroState,
} from '../game/types';
import type { Locale } from '../i18n';
import type { Translations } from '../i18n/types';

// Catalog-aligned identifiers (mirror backend/internal/worldevent).
export const EventQuestAlms = 'event.quest.alms.v1';
export const EventSocialPass = 'event.social.pass.v1';
export const EventPOIInnRest = 'event.poi.inn_rest.v1';
export const EventPOITavernRumor = 'event.poi.tavern_rumor.v1';
export const EventPOIVillageStop = 'event.poi.village_stop.v1';

// Wild excursion encounters (rolled during ExcursionWild phase).
export const EventWildAbandonedVillage = 'event.wild.abandoned_village.v1';
export const EventWildRuinedTemple = 'event.wild.ruined_temple.v1';
export const EventWildLostChest = 'event.wild.lost_chest.v1';
export const EventWildSanctuary = 'event.wild.sanctuary.v1';
export const EventWildRobbedCamp = 'event.wild.robbed_camp.v1';

export type WildEventId =
  | typeof EventWildAbandonedVillage
  | typeof EventWildRuinedTemple
  | typeof EventWildLostChest
  | typeof EventWildSanctuary
  | typeof EventWildRobbedCamp;

export const WildEventIds: readonly WildEventId[] = [
  EventWildAbandonedVillage,
  EventWildRuinedTemple,
  EventWildLostChest,
  EventWildSanctuary,
  EventWildRobbedCamp,
] as const;

// Stable wild-encounter action keys (mirror worldevent.Action* constants).
export const WildActionSearch = 'search';
export const WildActionPray = 'pray';
export const WildActionOpen = 'open';
export const WildActionRest = 'rest';
export const WildActionLeave = 'leave';

export const EncounterRoadNPC = 'encounter.road_npc.v1';
export const EncounterRoadPOI = 'encounter.road_poi.v1';
export const EncounterPlayerMeet = 'encounter.player_meet.v1';
export const EncounterWild = 'encounter.wild.v1';

/**
 * UI-facing state for the active world event session. Handlers produce a
 * discriminated variant; `App.tsx` reads it to decide which popup/panel to
 * render and the handler's `onLine`/`onReward`/`onEnd` mutate it.
 */
export type ActiveWorldEvent =
  | {
      kind: 'questAlms';
      eventId: typeof EventQuestAlms;
      sessionId: string;
      npcName: string;
      message: string;
      phase: 'approach' | 'shop';
      offers: Array<{ slotIndex: number; cost: number; sold: boolean }>;
    }
  | {
      kind: 'socialPass';
      eventId: typeof EventSocialPass;
      sessionId: string;
      partnerName: string;
      anySideOnline: boolean;
      maxChars: number;
      /** Whether the chat panel should be visible (dialogue phase). */
      chatVisible: boolean;
    }
  | {
      kind: 'wild';
      eventId: WildEventId;
      sessionId: string;
      /** Stable family slug — abandoned_village, ruined_temple, etc. */
      family: string;
      /** Allowed action keys (`search`, `pray`, `open`, `rest`, `leave`). */
      actions: string[];
      /** Server-side timeout (ISO timestamp) when the prompt auto-resolves. */
      promptEndsAt?: string;
    }
  | {
      kind: 'roadPoi';
      eventId:
        | typeof EventPOIInnRest
        | typeof EventPOITavernRumor
        | typeof EventPOIVillageStop;
      sessionId: string;
      actions: string[];
      promptEndsAt?: string;
      durationMs?: number;
    };

/** Locale snapshot consumed by handlers (re-read on each event). */
export interface WorldEventLocaleSnapshot {
  locale: Locale;
  tr: Translations;
}

/**
 * Context handed to every handler call. Wired by `App.tsx` once at startup.
 */
export interface WorldEventContext {
  ws: () => GameWebSocket | null;
  engine: () => GameEngine | null;
  hero: () => HeroState | null;
  locale: () => WorldEventLocaleSnapshot;
  setActive: (next: ActiveWorldEvent | null) => void;
  getActive: () => ActiveWorldEvent | null;
  appendLogClientMessage: (message: string) => void;
  refreshEquipment: () => void;
  triggerHaptic: (kind: 'success' | 'warning' | 'error') => void;
}

/**
 * Per-event behaviour. Adding a new event = new file + register({...}) call.
 */
export interface WorldEventHandler {
  eventId: string;
  onStart: (payload: WorldEventStartPayload, ctx: WorldEventContext) => void;
  onLine: (payload: WorldEventLinePayload, ctx: WorldEventContext) => void;
  onReward?: (payload: WorldEventRewardPayload, ctx: WorldEventContext) => void;
  onEnd: (payload: WorldEventEndPayload, ctx: WorldEventContext) => void;
}
